/**
 * Storage path ownership for Photobooth device actions (KAN-517).
 *
 * Rule: a device action may delete a Storage object only when the object lives under that device's own
 * folder (`Photobooth/{deviceId}/`), or under `Photobooth/global/` when the action runs in the global editor.
 * Anything else (the global library from a device, another device, an unparseable URL) is never deleted;
 * only the Firestore reference is removed.
 *
 * Pure functions only, no Angular and no Firebase imports.
 */

export type StorageOwnership = 'own' | 'global' | 'other';
export type StorageDeleteOutcome = 'deleted' | 'reference-only';

/** Confirmed wording (PO, KAN-517). Single source: components must not repeat these strings. */
export const STORAGE_DELETE_MESSAGES = {
  confirmOwn: 'Delete this file from this machine? This cannot be undone.',
  confirmReference:
    'This file belongs to the shared global library. It will be removed from this machine only. The global file is not deleted.',
  resultReferenceRemoved: 'Removed from this machine. The shared global file was not changed.',
  emptyAssets: 'No artwork uploaded for this machine yet.'
} as const;

const PHOTOBOOTH_ROOT = 'Photobooth/';

/** A usable device id is a single plain path segment (no separators, no dot segments, not the reserved names). */
function isSafeSegment(value: string): boolean {
  return (
    !!value &&
    value === value.trim() &&
    !value.includes('/') &&
    !value.includes('\\') &&
    !value.includes('%') &&
    value !== '.' &&
    value !== '..'
  );
}

/**
 * The storage folder the current action may delete from, with a trailing slash, or null when the
 * scope cannot be trusted (empty or malformed device id, or a device named like a reserved folder).
 */
export function ownStoragePrefix(isGlobal: boolean, deviceId: string): string | null {
  if (isGlobal) return `${PHOTOBOOTH_ROOT}global/`;
  if (!isSafeSegment(deviceId) || deviceId === 'global' || deviceId === 'Published') return null;
  return `${PHOTOBOOTH_ROOT}${deviceId}/`;
}

/**
 * Extracts the object path from a Firebase Storage download URL
 * (`.../o/Photobooth%2Fglobal%2FVideos%2Fx.mp4?alt=media&token=...`), decoding the path exactly once.
 * Returns null when the URL is not a recognizable download URL.
 */
export function storagePathFromDownloadUrl(url: string | null | undefined): string | null {
  if (!url || typeof url !== 'string') return null;
  try {
    const pathname = new URL(url).pathname;
    const marker = '/o/';
    const idx = pathname.indexOf(marker);
    if (idx === -1) return null;
    const encoded = pathname.substring(idx + marker.length);
    if (!encoded) return null;
    return decodeURIComponent(encoded);
  } catch {
    return null;
  }
}

/** True when the path is structurally plain: no empty, dot or dot-dot segments. */
function hasPlainSegments(path: string): boolean {
  return path.split('/').every(seg => seg !== '' && seg !== '.' && seg !== '..');
}

/**
 * Classifies an object path relative to the action scope.
 * 'own' = safe to delete. 'global' = under Photobooth/global/ while acting for a device.
 * 'other' = another device, Published, anything else, or an untrusted scope.
 */
export function classifyStoragePath(path: string | null | undefined, isGlobal: boolean, deviceId: string): StorageOwnership {
  if (!path || !hasPlainSegments(path)) return 'other';
  const prefix = ownStoragePrefix(isGlobal, deviceId);
  if (prefix && path.startsWith(prefix) && path.length > prefix.length) return 'own';
  if (!isGlobal && path.startsWith(`${PHOTOBOOTH_ROOT}global/`)) return 'global';
  return 'other';
}

export function isOwnedStoragePath(path: string | null | undefined, isGlobal: boolean, deviceId: string): boolean {
  return classifyStoragePath(path, isGlobal, deviceId) === 'own';
}

/** Classifies the object a stored download URL points at. An unparseable URL is never 'own'. */
export function classifyStorageUrl(url: string | null | undefined, isGlobal: boolean, deviceId: string): StorageOwnership {
  return classifyStoragePath(storagePathFromDownloadUrl(url), isGlobal, deviceId);
}

/** Confirmation text for deleting a stored object: own file vs a reference to a file we do not own. */
export function deleteConfirmationFor(ownership: StorageOwnership): string {
  return ownership === 'own' ? STORAGE_DELETE_MESSAGES.confirmOwn : STORAGE_DELETE_MESSAGES.confirmReference;
}

/** Aggregates several delete outcomes: reference-only if any object had to be kept. */
export function combineDeleteOutcomes(outcomes: StorageDeleteOutcome[]): StorageDeleteOutcome {
  return outcomes.includes('reference-only') ? 'reference-only' : 'deleted';
}
